import React, { useMemo, useRef } from 'react'
import {
	DASH_PATTERN_GAP_MULTIPLIER,
	DASH_PATTERN_LENGTH_MULTIPLIER,
	DRAG_THRESHOLD,
	HIT_AREA_PADDING_PX,
	PLACEMENT_MODE_GLOW_COLOR,
	SELECTION_GLOW_BLUR,
	SNAP_LINE_LENGTH_PX,
	SNAP_POINT_FILL_COLOR,
	SNAP_POINT_STROKE_COLOR,
	TERMINAL_CLICK_THRESHOLD_PX,
} from '@/constants'
import { convertSVGEventToCoordinates, distance, FieldCoordinateSystem } from '@/utils'
import type { Coordinate, Drawing } from '@/types'
import {
	findClosestSegmentPosition,
	getPathTangentAtPoint,
	getPoint,
	isPointNearControlPoint,
	splitPathAtPoint,
} from '@/utils/Drawing.utils'
import { BasePath } from './BasePath'
import { buildSVGPath } from '@/utils/SvgPath.utils'
import { useTheme } from '@/contexts/SettingsContext'
import { getThemeAwareColor } from '@/utils/ColorUtils'

const SEGMENT_HIT_THRESHOLD_PX = 15

type EditablePlayRendererProps = {
	drawing: Drawing
	coordSystem: FieldCoordinateSystem
	className?: string
	onSelect?: (id: string) => void
	onSelectWithPosition?: (id: string, position: { x: number; y: number }) => void
	isSelected?: boolean
	activeTool?: 'draw' | 'select' | 'erase' | 'fill' | 'addPlayer'
	onDelete?: (id: string) => void
	onDragStart?: (drawingId: string, feetX: number, feetY: number) => void
	onHover?: (isHovered: boolean) => void
	onPathContextMenu?: (
		drawingId: string,
		segmentIndex: number,
		insertPosition: Coordinate,
		pixelPosition: { x: number; y: number },
	) => void
	zoom?: number
	panX?: number
	panY?: number
	placementMode?: { type: 'presnap'; drawingId: string } | null
	onPlacementModeClick?: (
		drawingId: string,
		clickType: 'terminal' | 'path',
		point: Coordinate,
		pointId?: string,
	) => void
}

export function EditablePlayRenderer({
										 drawing,
										 coordSystem,
										 className,
										 onSelect,
										 onSelectWithPosition,
										 isSelected,
										 activeTool,
										 onDelete,
										 onDragStart,
										 onHover,
										 onPathContextMenu,
										 zoom = 1,
										 panX = 0,
										 panY = 0,
										 placementMode = null,
										 onPlacementModeClick,
									 }: EditablePlayRendererProps) {
	const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null)
	const { theme } = useTheme()

	// Transform function for BasePath
	const transformPoint = useMemo(() => {
		return (point: Coordinate): Coordinate => {
			return coordSystem.feetToPixels(point.x, point.y)
		}
	}, [coordSystem])

	// Computed values
	const strokeWidth = drawing.style.strokeWidth * coordSystem.scale
	const isInPlacementMode = placementMode?.drawingId === drawing.id
	const isShift = drawing.preSnapMotion?.type === 'shift'
	const isMotionSplit = drawing.preSnapMotion?.type === 'motion' && drawing.preSnapMotion.snapPointId

	// For shift type, entire path is dashed
	const effectiveLineStyle = isShift ? 'dashed' : drawing.style.lineStyle

	// Filter for glow effects
	const pathFilter = isInPlacementMode
		? `url(#placement-glow-${drawing.id})`
		: isSelected
			? `url(#glow-${drawing.id})`
			: undefined

	// Get linked point for indicator
	const linkedPoint = drawing.linkedPointId
		? getPoint(drawing, drawing.linkedPointId)
		: null
	const linkedPixel = linkedPoint
		? coordSystem.feetToPixels(linkedPoint.x, linkedPoint.y)
		: null

	// Memoize SVG path generation (expensive calculation)
	const pathData = useMemo(() => {
		return buildSVGPath(drawing, transformPoint)
	}, [drawing, transformPoint])

	function handlePlacementModeClick(
		event: React.MouseEvent,
		screenX: number,
		screenY: number,
		feetPos: Coordinate,
	): boolean {
		if (!placementMode || !onPlacementModeClick) return false

		// Check if click is near a terminal node
		const terminalPoints = Object.values(drawing.points).filter(p =>
			p.type === 'start' || p.type === 'end',
		)

		for (const termPoint of terminalPoints) {
			const pixelPoint = coordSystem.feetToPixels(termPoint.x, termPoint.y)
			const dist = distance({ x: screenX, y: screenY }, pixelPoint)

			if (dist < TERMINAL_CLICK_THRESHOLD_PX) {
				onPlacementModeClick(drawing.id, 'terminal', { x: termPoint.x, y: termPoint.y }, termPoint.id)
				return true
			}
		}

		// Check if click is near the path for snap point placement
		const segmentResult = findClosestSegmentPosition(drawing, feetPos, coordSystem)
		if (segmentResult && segmentResult.distance < SEGMENT_HIT_THRESHOLD_PX) {
			onPlacementModeClick(drawing.id, 'path', segmentResult.insertPosition)
			return true
		}

		return true // Consumed but no action
	}

	function handleClick(event: React.MouseEvent) {
		if (activeTool === 'erase' && onDelete) {
			onDelete(drawing.id)
			return
		}

		// Check if this was a drag (moved more than threshold)
		if (mouseDownPosRef.current) {
			const dist = distance(
				{ x: event.clientX, y: event.clientY },
				mouseDownPosRef.current,
			)
			if (dist > DRAG_THRESHOLD) {
				mouseDownPosRef.current = null
				return
			}
		}
		mouseDownPosRef.current = null

		// Handle placement mode clicks
		if (placementMode) {
			const coords = convertSVGEventToCoordinates(
				event,
				(event.currentTarget as SVGElement).ownerSVGElement,
				coordSystem,
				zoom,
				panX,
				panY,
			)
			if (!coords || !coords.feet) return
			if (handlePlacementModeClick(event, coords.screen.x, coords.screen.y, coords.feet)) return
		}

		if (activeTool === 'select' && onSelectWithPosition) {
			onSelectWithPosition(drawing.id, { x: event.clientX, y: event.clientY })
		} else if (onSelect) {
			onSelect(drawing.id)
		}
	}

	function handlePointerDown(event: React.PointerEvent) {
		mouseDownPosRef.current = { x: event.clientX, y: event.clientY }

		if (activeTool === 'select') {
			const coords = convertSVGEventToCoordinates(
				event,
				(event.target as SVGElement).ownerSVGElement,
			)
			if (!coords) return
			const nearNode = isPointNearControlPoint(
				drawing,
				coordSystem,
				coords.screen,
				12,
			)
			if (nearNode) return

			if (onDragStart) {
				// BUG FIX: screenX and screenY were undefined - should use coords.screen
				const feet = coordSystem.screenToFeet(coords.screen.x, coords.screen.y, zoom, panX, panY)
				onDragStart(drawing.id, feet.x, feet.y)
			}
		}
	}

	function handleContextMenu(position: Coordinate) {
		if (!onPathContextMenu) return

		const feetPos = coordSystem.screenToFeet(position.x, position.y, zoom, panX, panY)
		const result = findClosestSegmentPosition(drawing, feetPos, coordSystem)
		if (result && result.distance < SEGMENT_HIT_THRESHOLD_PX) {
			onPathContextMenu(
				drawing.id,
				result.segmentIndex,
				result.insertPosition,
				{ x: position.x, y: position.y },
			)
		}
	}

	function renderExtras() {
		const extras: React.ReactNode[] = []

		// Placement mode glow filter
		if (isInPlacementMode) {
			extras.push(
				<defs key="placement-filter">
					<filter
						id={`placement-glow-${drawing.id}`}
						x="-50%"
						y="-50%"
						width="200%"
						height="200%"
					>
						<feGaussianBlur in="SourceAlpha" stdDeviation={SELECTION_GLOW_BLUR * 1.5} result="blur" />
						<feFlood floodColor={PLACEMENT_MODE_GLOW_COLOR} result="glowColor" />
						<feComposite in="glowColor" in2="blur" operator="in" result="coloredGlow" />
						<feMerge>
							<feMergeNode in="coloredGlow" />
							<feMergeNode in="coloredGlow" />
							<feMergeNode in="SourceGraphic" />
						</feMerge>
					</filter>
				</defs>,
			)
		}

		// Selection glow filter
		if (isSelected && !isInPlacementMode) {
			extras.push(
				<defs key="selection-filter">
					<filter
						id={`glow-${drawing.id}`}
						x="-50%"
						y="-50%"
						width="200%"
						height="200%"
					>
						<feGaussianBlur stdDeviation={SELECTION_GLOW_BLUR} result="coloredBlur" />
						<feMerge>
							<feMergeNode in="coloredBlur" />
							<feMergeNode in="SourceGraphic" />
						</feMerge>
					</filter>
				</defs>,
			)
		}

		// Hit area for select tool (using memoized path data)
		if (activeTool === 'select') {
			if (pathData.d) {
				const hitStrokeWidth = strokeWidth + HIT_AREA_PADDING_PX * 2
				extras.push(
					<path
						key="hit-area"
						d={pathData.d}
						fill="none"
						stroke="transparent"
						strokeWidth={hitStrokeWidth}
						strokeLinecap="round"
						strokeLinejoin="round"
						pointerEvents="all"
						style={{ cursor: 'move' }}
						onPointerDown={handlePointerDown}
						onContextMenu={(e) => {
							e.preventDefault()
							e.stopPropagation()
							const coords = convertSVGEventToCoordinates(
								e,
								e.currentTarget.ownerSVGElement,
							)
							if (!coords) return
							handleContextMenu(coords.screen)
						}}
					/>,
				)
			}
		}

		// Linked point indicator
		if (drawing.playerId && linkedPixel) {
			extras.push(
				<circle
					key="linked-point"
					cx={linkedPixel.x}
					cy={linkedPixel.y}
					r={4}
					fill="hsl(var(--action-button))"
					stroke="hsl(var(--action-button-foreground))"
					strokeWidth={1}
					pointerEvents="none"
				/>,
			)
		}

		return extras.length > 0 ? <>{extras}</> : null
	}

	function renderSnapPointIndicator() {
		if (drawing.preSnapMotion?.type !== 'motion' || !drawing.preSnapMotion.snapPointId) {
			return null
		}

		const snapPoint = drawing.points[drawing.preSnapMotion.snapPointId]
		if (!snapPoint) return null

		const snapPixel = coordSystem.feetToPixels(snapPoint.x, snapPoint.y)
		const tangentAngle = getPathTangentAtPoint(drawing, snapPoint, coordSystem)
		const perpAngle = tangentAngle + Math.PI / 2
		const lineLength = SNAP_LINE_LENGTH_PX

		return (
			<>
				<circle
					cx={snapPixel.x}
					cy={snapPixel.y}
					r={6}
					fill={SNAP_POINT_FILL_COLOR}
					stroke={SNAP_POINT_STROKE_COLOR}
					strokeWidth={2}
					pointerEvents="none"
				/>
				{activeTool !== 'draw' && (
					<line
						x1={snapPixel.x - lineLength * Math.cos(perpAngle)}
						y1={snapPixel.y - lineLength * Math.sin(perpAngle)}
						x2={snapPixel.x + lineLength * Math.cos(perpAngle)}
						y2={snapPixel.y + lineLength * Math.sin(perpAngle)}
						stroke={SNAP_POINT_FILL_COLOR}
						strokeWidth={2}
						strokeLinecap="round"
						pointerEvents="none"
					/>
				)}
			</>
		)
	}

	function renderSplitPath(
		pathD: string,
		isDashed: boolean,
		dashPattern: number[],
	) {
		const contextMenuHandler = (e: React.MouseEvent<SVGPathElement>) => {
			e.preventDefault()
			e.stopPropagation()
			const coords = convertSVGEventToCoordinates(
				e,
				e.currentTarget.ownerSVGElement,
			)
			if (!coords) return
			handleContextMenu(coords.screen)
		}

		return (
			<path
				d={pathD}
				fill="none"
				stroke={activeTool === 'erase' ? 'var(--erase-hover)' : getThemeAwareColor(drawing.style.color, theme)}
				strokeWidth={activeTool === 'erase' ? strokeWidth * 1.15 : strokeWidth}
				strokeLinecap="round"
				strokeLinejoin="round"
				strokeDasharray={isDashed ? dashPattern.join(' ') : ''}
				opacity={isSelected ? 0.9 : 1}
				filter={pathFilter}
				style={activeTool === 'select' ? { cursor: 'move' } : {}}
				pointerEvents="all"
				onPointerDown={handlePointerDown}
				onContextMenu={contextMenuHandler}
			/>
		)
	}

	function renderMotionSplitPaths() {
		if (!isMotionSplit) return null

		const snapPointId = drawing.preSnapMotion?.snapPointId
		if (!snapPointId) return null

		const snapPoint = drawing.points[snapPointId]
		if (!snapPoint) return null

		const { preSnapPath, postSnapPath } = splitPathAtPoint(drawing, snapPoint, coordSystem)
		const dashPattern = [
			strokeWidth * DASH_PATTERN_LENGTH_MULTIPLIER,
			strokeWidth * DASH_PATTERN_GAP_MULTIPLIER,
		]

		return (
			<>
				{preSnapPath && renderSplitPath(preSnapPath, true, dashPattern)}
				{postSnapPath && renderSplitPath(
					postSnapPath,
					drawing.style.lineStyle === 'dashed',
					dashPattern,
				)}
			</>
		)
	}

	// Build drawing with effective line style for shift type
	const effectiveDrawing = isShift
		? { ...drawing, style: { ...drawing.style, lineStyle: 'dashed' as const } }
		: drawing

	// Erase hover color override
	const strokeColorOverride = activeTool === 'erase' ? 'var(--erase-hover)' : undefined
	const opacityOverride = isSelected ? 0.9 : undefined

	return (
		<g
			className={className}
			onClick={handleClick}
			onMouseEnter={() => onHover?.(true)}
			onMouseLeave={() => onHover?.(false)}
		>
			{renderExtras()}

			{isMotionSplit ? (
				renderMotionSplitPaths()
			) : (
				<BasePath
					drawing={effectiveDrawing}
					transformPoint={transformPoint}
					strokeScale={coordSystem.scale}
					onClick={() => {
					}}
					onContextMenu={handleContextMenu}
					onDragStart={(pos) => {
						const feet = coordSystem.screenToFeet(pos.x, pos.y, zoom, panX, panY)
						onDragStart?.(drawing.id, feet.x, feet.y)
					}}
					strokeColorOverride={strokeColorOverride}
					opacityOverride={opacityOverride}
					filter={pathFilter}
					cursor={activeTool === 'select' ? 'move' : undefined}
				/>
			)}

			{renderSnapPointIndicator()}
		</g>
	)
}
